"use client";

import Link from "next/link";
import { Suspense, useMemo, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import styles from "./ScannerMissionControl.module.css";
import { useInternalVolumeAccess } from "@/lib/useInternalVolumeAccess";

const CATEGORIES = [
  {
    id: "entry-signals",
    label: "Entry Signals",
    tagline: "Fresh signals and active momentum — where the market is moving today.",
    scanners: [
      {
        label: "Liquidity Sweep",
        href: "/scanner/liquidity-sweep",
        description: "Previous trading session’s downside sweeps and bounces from the low, ranked by sweep percentage.",
        tag: "Previous Session",
        recommended: false,
      },
      {
        label: "Momentum Pulse",
        href: "/scanner/pulse",
        description: "RSI + MACD collapsed into one view. High conviction when both agree — the right place to start.",
        tag: "Combined",
        recommended: true,
      },
      {
        label: "Slingshot",
        href: "/scanner/slingshot",
        description: "Close crosses above the 4-EMA of Highs for the first time in 3 bars — a fresh momentum entry signal.",
        tag: "New Signal",
        recommended: true,
      },
      {
        label: "RSI Cross",
        href: "/scanner/rsi",
        description: "Catch stocks the moment RSI reclaims a level. Early entry with confirmed momentum shift.",
        tag: "Daily",
        recommended: false,
      },
      {
        label: "MACD Turn",
        href: "/scanner/macd",
        description: "Histogram flips from negative to positive — earliest MACD confirmation of a trend change.",
        tag: "Daily",
        recommended: false,
      },
    ],
  },
  {
    id: "volume",
    label: "Volume",
    tagline: "Participation, effort, and acceptance — where traded volume confirms, warns, or exposes hidden pressure.",
    scanners: [
      {
        label: "Volume Intelligence",
        href: "/scanner/volume-intelligence",
        description: "Combined internal volume console for breakouts, dry-ups, accumulation, distribution, climax, OBV, and level tests.",
        tag: "Internal",
        recommended: true,
      },
      {
        label: "Volume Anomalies",
        href: "/scanner/volume",
        description: "Find unusual turnover using RVOL, volume Z-score, liquidity, and price impact. Best for spotting fresh activity.",
        tag: "RVOL",
        recommended: true,
      },
      {
        label: "Volume Divergence",
        href: "/scanner/volume-divergence",
        description: "Find price moves that OBV and participation do not confirm, plus high-volume absorption setups.",
        tag: "OBV",
        recommended: true,
      },
      {
        label: "Volume Profile",
        href: "/scanner/volume-profile",
        description: "Estimated POC, VAH/VAL, HVN/LVN, and value-area acceptance from daily PSX volume.",
        tag: "Profile",
        recommended: true,
      },
      {
        label: "Klinger Oscillator",
        href: "/scanner/klinger",
        description: "Volume-force trend turns using KVO signal-line crosses, filtered for stronger bullish and bearish setups.",
        tag: "KVO",
        recommended: false,
      },
    ],
  },
  {
    id: "trend-structure",
    label: "Trend & Structure",
    tagline: "EMA alignment, trendlines, and support/resistance — the bigger picture behind any signal.",
    scanners: [
      {
        label: "EMA Status",
        href: "/scanner/advanced",
        description: "Full EMA matrix (9/21/44/100/200) across daily, weekly, and monthly. See the complete trend stack at a glance.",
        tag: "Multi-TF",
        recommended: true,
      },
      {
        label: "Trendline Support",
        href: "/scanner/trendline-support",
        description: "Stocks sitting at or near auto-detected pivot-low support lines from the stock chart.",
        tag: "Structure",
        recommended: true,
      },
      {
        label: "Horizontal S/R",
        href: "/scanner/horizontal-sr",
        description: "Repeated pivot reactions clustered into durable support and resistance zones, with volume and confluence context.",
        tag: "Structure",
        recommended: true,
      },
      {
        label: "Resistance Breakouts",
        href: "/scanner/trendline-resistance-breakout",
        description: "Stocks closing above auto-detected pivot-high resistance lines with RVOL context.",
        tag: "Breakout",
        recommended: true,
      },
      {
        label: "Price Action",
        href: "/scanner/price-action",
        description: "Breakouts and structure setups based on candle and bar patterns.",
        tag: "Daily",
        recommended: true,
      },
      {
        label: "Near Key MAs",
        href: "/scanner/ma",
        description: "Stocks within 2% of a selected SMA or EMA. Best for finding pullback entries at support.",
        tag: "Daily",
        recommended: false,
      },
    ],
  },
  {
    id: "candlestick-patterns",
    label: "Candlestick Patterns",
    tagline: "All candlestick pattern scanners — single-candle, reversal, exhaustion, and candle-vs-trend context.",
    scanners: [
      {
        label: "Pattern Scanner",
        href: "/data/patterns",
        description: "Bullish, bearish, and contextual candle patterns with trend filter. The broadest pattern view.",
        tag: "Daily",
        recommended: true,
      },
      {
        label: "Reversal Scanner",
        href: "/data/reversal",
        description: "Grouped reversal pattern setups — hammers, engulfing, and multi-candle reversals.",
        tag: "Daily",
        recommended: false,
      },
      {
        label: "Doji Scanner",
        href: "/data/doji",
        description: "Doji and exhaustion candles — indecision at key levels often signals a pause or reversal.",
        tag: "Daily",
        recommended: false,
      },
      {
        label: "Trend Structure",
        href: "/data/structure",
        description: "Structure vs candle alignment — where candle signals sit within the broader trend.",
        tag: "Daily",
        recommended: false,
      },
    ],
  },
  {
    id: "patterns",
    label: "Patterns",
    tagline: "Non-candle chart patterns and structure tools — divergences, wave counts, Renko, and P&F views.",
    scanners: [
      {
        label: "RSI Divergences",
        href: "/data/divergences",
        description: "Price making new lows while RSI doesn't — classic early reversal warning.",
        tag: "Daily",
        recommended: false,
      },
      {
        label: "Elliott Impulse",
        href: "/scanner/elliott",
        description: "Stocks showing impulse wave structure. Useful for sizing and wave-count confirmation.",
        tag: "Daily",
        recommended: false,
      },
      {
        label: "Renko P&F",
        href: "/scanner/renko-pnf",
        description: "Brick-based structure with clean trend and reversal context.",
        tag: "Daily",
        recommended: false,
      },
      {
        label: "Point & Figure",
        href: "/scanner/pnf",
        description: "Column-based breakout charts that filter out noise.",
        tag: "Daily",
        recommended: false,
      },
      {
        label: "Renko First Green",
        href: "/scanner/renko-first-green",
        description: "First green brick after a downtrend — early reversal with volume context.",
        tag: "Daily",
        recommended: false,
      },
    ],
  },
  {
    id: "composite",
    label: "Composite",
    tagline: "Scored and ranked screens combining multiple signals — highest conviction setups first.",
    scanners: [
      {
        label: "Minervini Scanner",
        href: "/scanner/minervini",
        description: "Two-stage Minervini filter: Trend Template (Stage 1) + VCP detection (Stage 2). Sorted by RS rating.",
        tag: "VCP",
        recommended: true,
      },
      {
        label: "Smart Scan",
        href: "/scanner/smart-scan",
        description: "Multi-signal confluence board. Bullish setups ranked by technical + flow score.",
        tag: "Composite",
        recommended: true,
      },
      {
        label: "Next-Day Setups",
        href: "/scanner/premium",
        description: "Top-scored composite longs for the next session. Updated end of day.",
        tag: "Premium",
        recommended: false,
      },
    ],
  },
  {
    id: "fundamental",
    label: "Fundamental",
    tagline: "Value, quality, and institutional flow filters for longer holding periods.",
    scanners: [
      {
        label: "Long Term Holdings",
        href: "/scanner/long-term-holdings",
        description: "Three-pillar composite score (Value + Quality + Institutional Flow). Surfaces stocks with low PE, strong fundamentals, and institutional accumulation.",
        tag: "Fundamental",
        recommended: true,
      },
    ],
  },
] as const;

type CategoryId = (typeof CATEGORIES)[number]["id"];
type Scanner = {
  label: string;
  href: string;
  description: string;
  tag: string;
  recommended: boolean;
};
type Category = {
  id: CategoryId;
  label: string;
  tagline: string;
  scanners: readonly Scanner[];
};

function scannerBestFor(scanner: Scanner, category: Category): string {
  const label = scanner.label.toLowerCase();
  if (label.includes("momentum") || label.includes("slingshot") || label.includes("rsi")) return "Entry timing";
  if (label.includes("volume")) return "Participation check";
  if (label.includes("trendline") || label.includes("resistance") || label.includes("breakout")) return "Breakout structure";
  if (label.includes("ema") || label.includes("ma")) return "Trend health";
  if (label.includes("minervini") || label.includes("smart") || label.includes("next-day")) return "Ranked shortlist";
  if (category.id === "fundamental") return "Holding candidates";
  if (category.id === "candlestick-patterns") return "Candle setup";
  if (category.id === "patterns") return "Pattern context";
  return "Scanner workflow";
}

function scannerTimeframe(scanner: Scanner, category: Category): string {
  if (scanner.tag === "Premium") return "Next session";
  if (category.id === "fundamental") return "Longer term";
  if (scanner.tag === "Multi-TF") return "Multi-timeframe";
  return "Daily";
}

function ScannerContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { allowed: canSeeInternalVolume } = useInternalVolumeAccess();
  const categories = canSeeInternalVolume
    ? CATEGORIES
    : CATEGORIES.filter((c) => c.id !== "volume");

  const rawCategory = searchParams?.get("category") ?? "all";
  const validIds = ["all", ...categories.map((c) => c.id)];
  const activeCategoryId = validIds.includes(rawCategory) ? rawCategory : "all";

  const [query, setQuery] = useState("");

  const visibleCategories = useMemo(() => {
    const q = query.trim().toLowerCase();
    const cats =
      activeCategoryId === "all"
        ? [...categories]
        : categories.filter((c) => c.id === activeCategoryId);
    if (!q) return cats.map((c) => ({ ...c, scanners: [...c.scanners] }));
    return cats
      .map((c) => ({
        ...c,
        scanners: c.scanners.filter((s) => {
          const hay = `${c.label} ${c.tagline} ${s.label} ${s.description} ${s.tag}`.toLowerCase();
          return hay.includes(q);
        }),
      }))
      .filter((c) => c.scanners.length > 0);
  }, [categories, activeCategoryId, query]);

  const setCategory = (id: string) => {
    const params = new URLSearchParams(searchParams?.toString() ?? "");
    params.set("category", id);
    params.delete("scanner");
    params.delete("tab");
    router.replace(`/scanner?${params.toString()}`, { scroll: false });
  };

  return (
    <main className={styles.shell}>
      <div className={styles.topBar}>
        <div className={styles.topBarLeft}>
          <p className={styles.eyebrow}>Scanner console</p>
          <h1>Find a scanner</h1>
        </div>
        <label className={styles.searchBox}>
          <span className="sr-only">Search scanners</span>
          <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M10.8 18.1a7.3 7.3 0 1 1 0-14.6 7.3 7.3 0 0 1 0 14.6ZM16.1 16.1 21 21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search scanners..."
          />
        </label>
      </div>

      <nav className={styles.tabBar} aria-label="Scanner categories">
        <button
          type="button"
          className={activeCategoryId === "all" ? styles.tabActive : styles.tab}
          onClick={() => setCategory("all")}
        >
          All
        </button>
        {categories.map((c) => (
          <button
            key={c.id}
            type="button"
            className={activeCategoryId === c.id ? styles.tabActive : styles.tab}
            onClick={() => setCategory(c.id)}
          >
            {c.label}
          </button>
        ))}
      </nav>

      <div className={styles.content}>
        {visibleCategories.map((category) => (
          <section key={category.id} className={styles.categorySection}>
            {(activeCategoryId === "all" || query.trim().length > 0) && (
              <div className={styles.categoryHeader}>
                <h2>{category.label}</h2>
                <p>{category.tagline}</p>
              </div>
            )}
            <div className={styles.cardsGrid}>
              {category.scanners.map((scanner) => (
                <Link
                  key={scanner.href}
                  href={scanner.href}
                  className={`${styles.card} ${scanner.recommended ? styles.cardFeatured : ""}`}
                >
                  <div className={styles.cardTopline}>
                    <span className={styles.cardTag}>{scanner.tag}</span>
                    {scanner.recommended && (
                      <span className={styles.cardBadge}>Start here</span>
                    )}
                  </div>
                  <h3 className={styles.cardName}>{scanner.label}</h3>
                  <p className={styles.cardDesc}>{scanner.description}</p>
                  <div className={styles.cardFooter}>
                    <span className={styles.cardMeta}>
                      {scannerBestFor(scanner, category as Category)} · {scannerTimeframe(scanner, category as Category)}
                    </span>
                    <svg className={styles.cardArrow} aria-hidden="true" width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M5 2.5L9.5 7L5 11.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ))}
        {visibleCategories.length === 0 && (
          <p className={styles.emptyState}>No scanner matched that search.</p>
        )}
      </div>
    </main>
  );
}

function ScannerSkeleton() {
  return (
    <div className={styles.shell}>
      <div className={styles.skeletonTop}>
        <div className="skeleton" style={{ height: "56px", width: "200px", borderRadius: "8px" }} />
        <div className="skeleton" style={{ height: "44px", width: "240px", borderRadius: "8px" }} />
      </div>
      <div className="skeleton" style={{ height: "44px", borderRadius: "8px", marginTop: "1.5rem" }} />
      <div className={styles.skeletonGrid}>
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="skeleton" style={{ height: "180px", borderRadius: "12px" }} />
        ))}
      </div>
    </div>
  );
}

export default function ScannerMissionControlClient() {
  return (
    <div className={styles.page}>
      <Suspense fallback={<ScannerSkeleton />}>
        <ScannerContent />
      </Suspense>
    </div>
  );
}
